import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import FormPhoneInput from "../../Components/v2/Form/FormPhoneInput";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert } from "../../Components/Alert";
import { Button } from "../../Components/Button";
import { ModalConfirm } from "../../Components/ModalConfirm";
import Select from "react-select";
import {
  requestCreateBusiness,
  requestListBusiness,
  resetNewBusiness,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
} from "../../Context";
import { TAX_CATEGORIES } from "../../Constants";
import FormLabel from "../../Components/FormLabel";
import { classNames, generateArrayOfYears } from "../../Utils";
import { Field, Label, Switch } from "@headlessui/react";
import {
  BUSINESS_CATEGORIES,
  BUSINESS_ENTITY_TYPES,
  BUSINESS_TYPES_OPTIONS,
} from "../../Constants/business";
import { useFreeBusinessCreateBlocked } from "../../Hooks";

const years = generateArrayOfYears();

export const BusinessCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fieldError, setfieldError] = useState(false);
  const [showSuccessModal, setshowSuccessModal] = useState(false);
  const dispatch = useBusinessTransactionDispatch();
  const { newBusiness, error, loading, businesses } =
    useBusinessTransactionState();
  const { user } = useAuthState();
  const location = useLocation();
  const { onboarding } = location.state || {};

  // Free accounts may keep only 1 business — hard-block creating another and
  // steer to premium (mirrors the sidebar create modal). Onboarding / first
  // business (count 0) and premium members are never blocked. Membership comes
  // from live subscriptions, not the lagging `user.membership` flag.
  const createBlocked = useFreeBusinessCreateBlocked(businesses);

  const [businessName, setbusinessName] = useState("");
  const [type, settype] = useState("PERSONAL");
  const [entityType, setentityType] = useState("");
  const [category, setcategory] = useState(null);
  const [phone, setphone] = useState();
  const [address, setaddress] = useState("");
  const [taxCategory, settaxCategory] = useState(null);
  const [alwaysSync, setalwaysSync] = useState(false);
  const [npwp, setnpwp] = useState("");
  const [yearRegistered, setyearRegistered] = useState("");
  const [email, setemail] = useState("");

  const [businessError, setbusinessError] = useState(null);
  const [typeError, settypeError] = useState(null);
  const [phoneError, setphoneError] = useState(null);
  const [emailError, setemailError] = useState(null);
  const [npwpError, setnpwpError] = useState(null);
  const [submitted, setsubmitted] = useState(false);

  const cleanedPhone = phone?.replace(/\s/g, "");
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const navigateToTransaction = () => {
    setshowSuccessModal(false);
    if (!error) {
      if (newBusiness?.id) {
        if (onboarding) {
          navigate(`/coa-onboarding/${newBusiness?.id}`);
        } else {
          navigate(-1);
        }
      }
    }
  };

  useEffect(() => {
    return () => {
      resetNewBusiness(dispatch);
    };
  }, [dispatch]);

  // Load existing businesses to know whether the free-limit warning applies.
  useEffect(() => {
    if (user?.id) {
      requestListBusiness(dispatch, { owner: user.id });
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    if (newBusiness?.id) {
      setshowSuccessModal(true);
    }
  }, [newBusiness.id]);

  useEffect(() => {
    if (submitted) {
      const isBusinessValid = Boolean(businessName);
      const isTypeValid = Boolean(type);

      if (isBusinessValid) {
        setErrorMessage("business", "");
      } else {
        setErrorMessage("business", "Business name is required.");
      }

      if (isTypeValid) {
        setErrorMessage("type", "");
      } else {
        setErrorMessage("type", "Business Type name is required.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessName, type, submitted]);

  useEffect(() => {
    if (submitted && phone) {
      const isPhoneValid = cleanedPhone?.length >= 10;
      if (isPhoneValid) {
        setErrorMessage("phone", "");
      } else {
        setErrorMessage("phone", "Please enter a valid phone number.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phone, submitted]);

  useEffect(() => {
    if (submitted && email) {
      const isEmailValid = emailRegex.test(email);
      if (isEmailValid) {
        setErrorMessage("email", "");
      } else {
        setErrorMessage("email", "Please enter a valid email address.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, submitted]);

  useEffect(() => {
    if (submitted && npwp) {
      const isNpwpValid = npwp.length >= 15 && npwp.length <= 16;
      if (isNpwpValid) {
        setErrorMessage("npwp", "");
      } else {
        setErrorMessage("npwp", "Invalid NPWP. Enter 15-16 digits.");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [npwp, submitted]);

  useEffect(() => {
    if (type !== "BUSINESS") {
      setentityType("");
    }
  }, [type]);

  const setErrorMessage = (field, message) => {
    switch (field) {
      case "business":
        setbusinessError(message);
        break;
      case "type":
        settypeError(message);
        break;
      case "phone":
        setphoneError(message);
        break;
      case "email":
        setemailError(message);
        break;
      case "npwp":
        setnpwpError(message);
        break;
      default:
        break;
    }
  };

  const validateInput = () => {
    const isBusinessValid = Boolean(businessName);
    const isTypeValid = Boolean(type);
    const isPhoneValid = phone ? cleanedPhone?.length >= 10 : true;
    const isEmailValid = email ? emailRegex.test(email) : true;
    const isNpwpValid = npwp ? npwp.length >= 15 && npwp.length <= 16 : true;

    return (
      isBusinessValid &&
      isTypeValid &&
      isPhoneValid &&
      isEmailValid &&
      isNpwpValid
    );
  };

  function onSave(event) {
    event.preventDefault();
    if (createBlocked) return;
    setsubmitted(true);
    const valid = validateInput();
    const { organization, type, phone, taxCategory, address } =
      event.target.elements;
    if (valid) {
      setfieldError(false);
      requestCreateBusiness(dispatch, {
        input: {
          name: organization.value,
          email,
          type: type.value,
          structure: entityType.value || null,
          category: category?.value || null,
          phone: phone.value,
          taxCategory: taxCategory.value || null,
          address: address.value,
          taxNumber: npwp,
          yearRegistered: yearRegistered.value,
          alwaysSync,
        },
      });
    } else {
      setfieldError(true);
    }
  }
  return (
    <div className="bg-white shadow sm:overflow-hidden rounded-md sm:rounded-lg">
      {newBusiness?.id && (
        <ModalConfirm
          open={showSuccessModal}
          setOpen={(open) => {
            setshowSuccessModal(open);
          }}
          title="Success"
          description="Your business has been created."
          type="SUCCESS"
          onConfirm={navigateToTransaction}
          data-testid="modal-open"
        />
      )}
      <form onSubmit={onSave}>
        <div className="space-y-6 px-4 py-5 sm:p-6">
          {createBlocked && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
              <h3 className="text-sm font-semibold text-amber-800">
                {t("business.messages.freeLimitBlockTitle")}
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                {t("business.messages.freeLimitBlockDesc")}
              </p>
              <button
                type="button"
                onClick={() => navigate("/subscription")}
                className="mt-3 inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {t("business.actions.subscribe")}
              </button>
            </div>
          )}
          {fieldError && (
            <Alert
              title="Incomplete Form"
              type="warning"
              message="Please fill out all fields."
            />
          )}
          {error && (
            <Alert
              title="Error"
              type="error"
              message={error?.message || "Error create forum."}
              errors={error?.errors}
            />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <FormLabel
                htmlFor="organization"
                label={t("Business Name")}
                required
              />
              <input
                type="text"
                name="organization"
                data-testid="organization"
                value={businessName}
                onChange={(e) => setbusinessName(e.target.value)}
                placeholder={t("Enter Business Name")}
                className={classNames(
                  businessError
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
              />
              {businessError && (
                <div className="mt-1 text-red-600 text-sm">
                  {t(businessError)}
                </div>
              )}
            </div>
            <div>
              <FormLabel htmlFor="type" label={t("Business Type")} required />
              <select
                id="type"
                name="type"
                data-testid="type"
                value={type}
                onChange={(e) => settype(e.target.value)}
                className={classNames(
                  typeError
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md border bg-white py-2 px-3 shadow-sm"
                )}
              >
                {BUSINESS_TYPES_OPTIONS.map((businessType) => (
                  <option key={businessType.value} value={businessType.value}>
                    {t(businessType.label)}
                  </option>
                ))}
              </select>
              {typeError && (
                <div className="mt-1 text-red-600 text-sm">{t(typeError)}</div>
              )}
            </div>
            <div>
              <FormLabel htmlFor="entityType" label={t("businessEntityType")} />
              <Select
                name="entityType"
                className="mt-1 text-sm"
                classNamePrefix="select2-selection"
                options={BUSINESS_ENTITY_TYPES}
                placeholder={t("businessEntityInputPlaceholder")}
                value={entityType}
                onChange={(value) => {
                  setentityType(value);
                }}
                isDisabled={type !== "BUSINESS"}
              />
            </div>
            <div>
              <FormLabel
                htmlFor="category"
                label={t("businessCategory", "Kategori Usaha")}
              />
              <Select
                name="category"
                id="category"
                className="mt-1 text-sm"
                classNamePrefix="select2-selection"
                options={BUSINESS_CATEGORIES}
                placeholder={t("businessCategoryPlaceholder", "Pilih Kategori Usaha")}
                value={category}
                onChange={setcategory}
                isClearable
              />
            </div>
            <div>
              <FormLabel htmlFor="taxCategory" label={t("Annual Revenue")} />
              <Select
                name="taxCategory"
                id="taxCategory"
                options={TAX_CATEGORIES.map((category) => ({
                  value: category.value,
                  label: t(category.label),
                }))}
                value={taxCategory}
                onChange={settaxCategory}
                className="mt-1 text-sm"
                classNamePrefix="select2-selection"
                placeholder={t("Select Annual Revenue")}
              />
              {typeError && (
                <div className="mt-1 text-red-600 text-sm">{t(typeError)}</div>
              )}
            </div>
            <div>
              <FormLabel
                htmlFor="npwp"
                label={t("Tax Identification Number (NPWP)")}
              />
              <NumericFormat
                type="text"
                name="npwp"
                id="npwp"
                data-testid="npwp"
                allowLeadingZeros
                className={classNames(
                  npwpError
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
                placeholder={t("Enter the tax identification number (NPWP)")}
                displayType="input"
                value={npwp || ""}
                onValueChange={(value) => {
                  setnpwp(value.value);
                }}
                isAllowed={({ value }) => value.length <= 16}
              />
              {npwpError && (
                <div className="mt-1 text-red-600 text-sm">{t(npwpError)}</div>
              )}
            </div>
            <div>
              <FormLabel htmlFor="year" label={t("npwpYearRegistered")} />
              <Select
                name="year"
                className="mt-1 text-sm"
                classNamePrefix="select2-selection"
                options={years}
                placeholder={t("npwpYearRegisteredPlaceholder")}
                value={yearRegistered}
                onChange={(value) => {
                  setyearRegistered(value);
                }}
              />
            </div>
            <div>
              <FormLabel htmlFor="email" label={t("Email")} />
              <input
                type="email"
                name="email"
                data-testid="email"
                value={email}
                placeholder={t("Enter email")}
                onChange={(e) => setemail(e.target.value)}
                className={classNames(
                  emailError
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
              />
              {emailError && (
                <div className="mt-1 text-red-600 text-sm">{t(emailError)}</div>
              )}
            </div>
            <div>
              <FormLabel htmlFor="phone" label={t("Phone Number")} />
              <FormPhoneInput
                id="phone"
                name="phone"
                data-testid="phone"
                value={phone}
                onChange={setphone}
                placeholder={t("Enter Phone Number")}
                className={classNames(
                  phoneError
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                  "mt-1 block w-full text-sm rounded-md shadow-sm"
                )}
              />
              {phoneError && (
                <div className="mt-1 text-red-600 text-sm">{t(phoneError)}</div>
              )}
            </div>
            <div>
              <FormLabel htmlFor="address" label={t("Address")} />
              <input
                id="address"
                type="text"
                name="address"
                data-testid="address"
                value={address}
                onChange={(e) => setaddress(e.target.value)}
                placeholder={t("Enter Address")}
                className="mt-1 block w-full text-sm rounded-md shadow-sm border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label
                htmlFor="accounting-sync-enabled"
                className="block text-sm font-medium text-gray-700"
              >
                {t("Sync")}
              </label>
              <Field
                as="div"
                className="flex items-center mt-1 py-2 px-3 border border-gray-300 rounded-md"
              >
                <Switch
                  checked={alwaysSync}
                  data-testid="always-sync-switch"
                  onChange={setalwaysSync}
                  className={classNames(
                    alwaysSync ? "bg-blue-600" : "bg-gray-200",
                    "relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  )}
                >
                  <span className="sr-only">Use setting</span>
                  <span
                    className={classNames(
                      alwaysSync ? "translate-x-5" : "translate-x-0",
                      "pointer-events-none relative inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                    )}
                  >
                    <span
                      className={classNames(
                        alwaysSync
                          ? "opacity-0 ease-out duration-100"
                          : "opacity-100 ease-in duration-200",
                        "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                      )}
                      aria-hidden="true"
                    >
                      <svg
                        className="h-3 w-3 text-gray-400"
                        fill="none"
                        viewBox="0 0 12 12"
                      >
                        <path
                          d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span
                      className={classNames(
                        alwaysSync
                          ? "opacity-100 ease-in duration-200"
                          : "opacity-0 ease-out duration-100",
                        "absolute inset-0 h-full w-full flex items-center justify-center transition-opacity"
                      )}
                      aria-hidden="true"
                    >
                      <svg
                        className="h-3 w-3 text-blue-600"
                        fill="currentColor"
                        viewBox="0 0 12 12"
                      >
                        <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                      </svg>
                    </span>
                  </span>
                </Switch>
                <Label as="span" className="ml-3">
                  <div>
                    <span className="text-sm text-gray-900">
                      {t("Always Synchronize Cash Book & Accounting")}
                    </span>
                  </div>
                </Label>
              </Field>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 rounded-b-md sm:rounded-b-lg px-4 py-6 text-right sm:px-6">
          <Button
            type="submit"
            loading={loading}
            disabled={loading || createBlocked}
            className="w-full sm:w-auto"
            title={t("Create Business")}
            data-testid="create-business"
          />
        </div>
      </form>
    </div>
  );
};
